import React from 'react'
import Ajv from 'ajv'
import addFormats from 'ajv-formats'
import type { JSONContent } from './types'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import { ThemeProvider } from '@mui/material/styles'
import {
  splitContentByPages,
  compileCurrentSchema,
  convertStylesToSx,
  getFieldNameByFieldId,
} from './utils'
import { collectConditions, buildCompiledJsonSchema } from './utils'
import { parseStyleString } from './utils'
import type { FormRenderProps, CurrentSchema } from './types'
import { RenderNode } from './components/RenderNode'
import { defaultStylesForButtons } from './index'
import { buildFormTheme } from './theme'

export function FormRender({
  content,
  onSubmit,
  layout,
  validationScheme,
  // sessionId is used by parent component for tracking
  sessionId: _sessionId,
  onPartialSubmit,
  initialValues,
  language = 'en',
}: FormRenderProps) {
  void _sessionId // Suppress unused variable warning
  void language // Suppress unused variable warning

  if (!content || !content.content) {
    return null
  }

  return (
    <FormRenderContent
      content={content}
      onSubmit={onSubmit}
      layout={layout}
      validationScheme={validationScheme}
      onPartialSubmit={onPartialSubmit}
      initialValues={initialValues}
    />
  )
}

function FormRenderContent({
  content,
  onSubmit,
  layout,
  validationScheme,
  onPartialSubmit,
  initialValues,
}: Omit<FormRenderProps, 'sessionId' | 'language'>) {
  // Парсим стили из layout
  const containerStyles = layout?.contianerStyles
    ? convertStylesToSx(parseStyleString(layout.contianerStyles))
    : {}
  const bodyStyles = layout?.bodyStyles
    ? convertStylesToSx(parseStyleString(layout.bodyStyles))
    : {}

  // Строим MUI theme из layout (парсинг и мерж вынесены в buildFormTheme)
  const muiTheme = React.useMemo(
    () => buildFormTheme(layout),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layout?.theme, layout?.bodyStyles]
  )

  // Выводим content в консоль для отладки
  // React.useEffect(() => {
  //   console.log('FormRender content:', JSON.stringify(content, null, 2))
  // }, [content])

  // console.log('FormRender fields:', JSON.stringify(layout?.fields, null, 3))

  // Состояние значений формы
  const [formValues, setFormValues] = React.useState<Record<string, any>>({})
  // Состояние ошибок валидации от схемы (Ajv)
  const [validationErrors, setValidationErrors] = React.useState<
    Record<string, string>
  >({})

  // Состояние кастомных ошибок валидации (от компонентов типа PhoneInputWithCountry)
  const [customFieldErrors, setCustomFieldErrors] = React.useState<
    Record<string, string>
  >({})

  // Объединяем ошибки для отображения в компонентах
  const allValidationErrors = React.useMemo(() => {
    return { ...validationErrors, ...customFieldErrors }
  }, [validationErrors, customFieldErrors])

  // Используем переданную validationScheme или строим её из content
  const compiledJsonSchema = React.useMemo(() => {
    if (validationScheme) {
      return validationScheme
    }

    // Если validationScheme не передан, строим её из content (для обратной совместимости)
    const conditions = collectConditions(content)

    return buildCompiledJsonSchema(content, conditions, layout)
  }, [content, validationScheme, layout])

  // Состояние для currentSchema (JSON-schema после применения всех условий)
  // Инициализируем его сразу на основе compiledJsonSchema и пустых значений формы,
  // чтобы условия (hide/show) применялись уже при первом рендере
  const [currentSchema, setCurrentSchema] = React.useState<CurrentSchema>(() =>
    compileCurrentSchema(compiledJsonSchema, {})
  )

  // Компилируем currentSchema при изменении formValues
  // Обрабатываем все if/then/allOf/anyOf и мержим в currentSchema
  React.useEffect(() => {
    const schema = compileCurrentSchema(compiledJsonSchema, formValues)
    setCurrentSchema(schema)
    // Выводим currentSchema в консоль для отладки
    // console.log('FormRender currentSchema:', JSON.stringify(schema, null, 2))
    // Выводим compiledJsonSchema в консоль для отладки
    // console.log(
    //   'FormRender compiledJsonSchema:',
    //   JSON.stringify(compiledJsonSchema, null, 2)
    // )
  }, [formValues, compiledJsonSchema])

  // Инициализация значений формы из defaultValue полей или initialValues (для восстановления сессии)
  React.useEffect(() => {
    // Если переданы initialValues (восстановленная сессия), используем их
    if (initialValues && Object.keys(initialValues).length > 0) {
      setFormValues(initialValues)

      return
    }

    const defaultValues: Record<string, any> = {}

    function collectFieldValues(node: JSONContent) {
      const fieldInputTypes = [
        'shortAnswerFieldInput',
        'longAnswerFieldInput',
        'numberFieldInput',
        'emailFieldInput',
        'phoneNumberFieldInput',
        'linkFieldInput',
        'dateFieldInput',
        'timeFieldInput',
        'dateTimeFieldInput',
        'fileUploaderFieldInput',
        'booleanFieldInput',
        'fieldChoiceOption',
        'fieldCheckboxOption',
        'fieldSelectOption',
        'fieldMultiselectOption',
      ]

      if (node.type && fieldInputTypes.includes(node.type)) {
        const attrs = node.attrs || {}
        const fieldId = attrs.fieldId || ''

        // Получаем настройки из layout.fields
        const fieldSettings = fieldId && layout?.fields?.[fieldId]

        // Для choice/checkbox опций используем getFieldNameByFieldId для поиска name
        // по fieldId, так как name хранится в первой опции или в связанном поле
        let name: string
        if (
          node.type === 'fieldChoiceOption' ||
          node.type === 'fieldCheckboxOption' ||
          node.type === 'fieldSelectOption' ||
          node.type === 'fieldMultiselectOption'
        ) {
          const foundName = getFieldNameByFieldId(content, fieldId, layout)
          name =
            foundName ||
            fieldSettings?.name ||
            attrs.name ||
            fieldId ||
            `field-${fieldId || Math.random().toString(36).substr(2, 9)}`
        } else {
          name =
            fieldSettings?.name ||
            attrs.name ||
            fieldId ||
            `field-${fieldId || Math.random().toString(36).substr(2, 9)}`
        }

        const defaultValue =
          fieldSettings?.defaultValue || attrs.defaultValue || ''
        const defaultValueEnabled =
          fieldSettings?.defaultValueEnabled ??
          attrs.defaultValueEnabled ??
          false

        // Определяем тип поля для правильной инициализации
        const isCheckbox = node.type === 'fieldCheckboxOption'
        const isMultiselect = node.type === 'fieldMultiselectOption'
        const isChoice = node.type === 'fieldChoiceOption'
        const isSelect = node.type === 'fieldSelectOption'
        const isBooleanField = node.type === 'booleanFieldInput'

        if (!(name in defaultValues)) {
          // Для checkbox и multiselect инициализируем массивом,
          // для boolean - false, для остальных - пустой строкой
          if (isCheckbox || isMultiselect) {
            defaultValues[name] = []
          } else if (node.type === 'fileUploaderFieldInput') {
            defaultValues[name] = null
          } else if (isBooleanField) {
            defaultValues[name] = false
          } else {
            defaultValues[name] = ''
          }
        }

        if (isChoice || isSelect) {
          if (defaultValueEnabled) {
            const optionValue = attrs.value || attrs.optionId
            if (optionValue) {
              defaultValues[name] = optionValue
            }
          }
        } else if (isCheckbox || isMultiselect) {
          // Убеждаемся, что значение - массив
          if (!Array.isArray(defaultValues[name])) {
            defaultValues[name] = []
          }
          if (defaultValueEnabled) {
            const optionIdOrValue = attrs.value || attrs.optionId
            if (
              optionIdOrValue &&
              !defaultValues[name].includes(optionIdOrValue)
            ) {
              defaultValues[name] = [...defaultValues[name], optionIdOrValue]
            }
          }
        } else if (isBooleanField) {
          defaultValues[name] =
            defaultValueEnabled && String(defaultValue) === 'true'
        } else if (defaultValueEnabled && defaultValue) {
          defaultValues[name] = defaultValue
        }
      }

      if (node.content) {
        node.content.forEach(collectFieldValues)
      }
    }

    if (content.content) {
      content.content.forEach(collectFieldValues)
    }

    setFormValues(defaultValues)
  }, [content, layout, initialValues])

  // Обработчик ошибок валидации от кастомных компонентов (например, PhoneInputWithCountry)
  const handleValidationError = React.useCallback(
    (fieldName: string, error: string) => {
      setCustomFieldErrors((prev) => {
        if (error) {
          // Добавляем ошибку в отдельное состояние для кастомных ошибок
          return { ...prev, [fieldName]: error }
        } else {
          // Удаляем ошибку, если она была
          const { [fieldName]: _removed, ...rest } = prev
          void _removed

          return rest
        }
      })
    },
    []
  )

  const handleFieldChange = React.useCallback(
    (name: string | undefined, value: any) => {
      if (!name) {
        return
      }
      setFormValues((prev) => {
        const newValues = {
          ...prev,
          [name]: value,
        }

        return newValues
      })

      // Очищаем ошибки валидации от схемы (Ajv) при изменении значения
      // customFieldErrors управляются отдельно через handleValidationError
      setValidationErrors({})
    },
    []
  )

  // Обработчик потери фокуса полем для автосохранения
  const handleBlur = React.useCallback(() => {
    if (onPartialSubmit) {
      onPartialSubmit(formValues)
    }
  }, [onPartialSubmit, formValues])

  const { pages, hasPages, thankYouPage } = splitContentByPages(content)
  const [currentStep, setCurrentStep] = React.useState(0)
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  // Флаг для отслеживания, нужно ли очищать ошибки при изменении страницы
  // Очищаем ошибки только если они не были установлены в handleNext
  const shouldClearErrorsOnStepChange = React.useRef(true)

  // Ссылка на корневую форму для скролла вверх
  const formRef = React.useRef<HTMLDivElement | null>(null)

  const scrollToFormTop = React.useCallback(() => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  // Очищаем ошибки валидации при изменении страницы
  // Это гарантирует, что ошибки с предыдущей страницы не отображаются на новой
  React.useEffect(() => {
    if (shouldClearErrorsOnStepChange.current) {
      setValidationErrors({})
    }
    shouldClearErrorsOnStepChange.current = true
  }, [currentStep])

  // Нормализуем значения формы перед валидацией:
  // - для числовых полей конвертируем строки в number
  // - пустые строки для числовых полей удаляем (поле считается не заполненным)
  const normalizeValuesForValidation = React.useCallback(
    (values: Record<string, any>, schema: any): Record<string, any> => {
      if (!schema || !schema.properties) {
        return values
      }

      const normalized: Record<string, any> = { ...values }
      const requiredFields = new Set<string>(schema.required || [])

      Object.entries(schema.properties as Record<string, any>).forEach(
        ([fieldName, fieldSchema]) => {
          if (!fieldSchema) {
            return
          }

          const rawValue = normalized[fieldName]

          // Числовые поля: конвертируем строку в number, пустые удаляем — чтобы не было "must be number"
          if (fieldSchema.type === 'number') {
            if (
              rawValue === '' ||
              rawValue === null ||
              rawValue === undefined
            ) {
              delete normalized[fieldName]

              return
            }

            if (typeof rawValue === 'string') {
              const parsed = Number(rawValue.replace(',', '.'))
              if (!Number.isNaN(parsed)) {
                normalized[fieldName] = parsed
              }
            }

            return
          }

          // Файловые поля (type: object) — если значение пустое, удаляем его,
          // чтобы Ajv не выдавал "must be object" для необязательных полей
          if (fieldSchema.type === 'object') {
            if (
              rawValue === '' ||
              rawValue === null ||
              rawValue === undefined
            ) {
              delete normalized[fieldName]
            }

            return
          }

          // Необязательные строковые поля с форматами (email, uri и т.п.):
          // если значение пустая строка — удаляем, чтобы Ajv не валидировал формат
          if (
            fieldSchema.type === 'string' &&
            fieldSchema.format &&
            !requiredFields.has(fieldName) &&
            (rawValue === '' || rawValue === null || rawValue === undefined)
          ) {
            delete normalized[fieldName]
          }
        }
      )

      return normalized
    },
    []
  )

  // Хардкод английских текстов вместо i18n
  const validationMessages = {
    required: 'This field is required',
    emailFormat: 'Please enter a valid email address',
    urlFormat: 'Please enter a valid URL',
    invalidType: 'Invalid value type',
    minLength: (min: number) => `Minimum ${min} characters required`,
    maxLength: (max: number) => `Maximum ${max} characters allowed`,
    minimum: (minimum: number) => `Value must be at least ${minimum}`,
    maximum: (maximum: number) => `Value must be at most ${maximum}`,
    pattern: 'Invalid format',
    minItems: (min: number) => `Select at least ${min} options`,
    maxItems: (max: number) => `Select at most ${max} options`,
  }

  const commonMessages = {
    previous: 'Previous',
    next: 'Next',
    submit: 'Submit',
  }

  // Функция для создания схемы валидации на основе полей текущей страницы
  // Используем compiledJsonSchema и применяем currentSchema для фильтрации
  // Вспомогательная функция для капитализации первой буквы сообщения об ошибке
  const capitalizeError = React.useCallback((message: string): string => {
    if (!message) {
      return message
    }

    return message.charAt(0).toUpperCase() + message.slice(1)
  }, [])

  const getLocalizedErrorMessage = React.useCallback(
    (error: any): string => {
      // Обрабатываем различные типы ошибок AJV
      if (error.keyword === 'minLength' && (error.params as any)?.limit === 1) {
        return validationMessages.required
      } else if (error.keyword === 'required') {
        return validationMessages.required
      } else if (
        error.keyword === 'minItems' &&
        (error.params as any)?.limit === 1
      ) {
        return validationMessages.required
      } else if (error.keyword === 'format') {
        const format = (error.params as any)?.format
        if (format === 'email') {
          return validationMessages.emailFormat
        } else if (format === 'uri' || format === 'url') {
          return validationMessages.urlFormat
        }
      } else if (error.keyword === 'type') {
        return validationMessages.invalidType
      } else if (error.keyword === 'minLength') {
        return validationMessages.minLength((error.params as any)?.limit)
      } else if (error.keyword === 'maxLength') {
        return validationMessages.maxLength((error.params as any)?.limit)
      } else if (error.keyword === 'minItems') {
        return validationMessages.minItems((error.params as any)?.limit)
      } else if (error.keyword === 'maxItems') {
        return validationMessages.maxItems((error.params as any)?.limit)
      } else if (error.keyword === 'minimum') {
        return validationMessages.minimum((error.params as any)?.limit)
      } else if (error.keyword === 'maximum') {
        return validationMessages.maximum((error.params as any)?.limit)
      } else if (error.keyword === 'pattern') {
        return validationMessages.pattern
      }

      // Fallback на стандартное сообщение AJV с капитализацией
      return capitalizeError(error.message || 'Validation error')
    },
    [validationMessages, capitalizeError]
  )

  const scrollToFirstErrorField = React.useCallback(
    (errors: Record<string, string>) => {
      const firstErrorField = Object.keys(errors)[0]
      if (!firstErrorField) {
        return
      }

      const errorElement = document.querySelector<HTMLElement>(
        `[data-field-name="${firstErrorField}"]`
      )
      if (errorElement) {
        errorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    },
    []
  )

  const createValidationSchema = React.useCallback(
    (pageContent: JSONContent[]): any => {
      // Создаем копию compiledJsonSchema
      const schema = JSON.parse(JSON.stringify(compiledJsonSchema))

      // Фильтруем properties и required по currentSchema
      const filteredProperties: Record<string, any> = {}
      const filteredRequired: string[] = []

      // Собираем имена полей на текущей странице
      const pageFieldNames = new Set<string>()
      const pageFieldIds = new Set<string>()

      // Сначала собираем fieldId из pageContent
      function collectPageFieldIds(node: JSONContent) {
        const fieldInputTypes = [
          'shortAnswerFieldInput',
          'longAnswerFieldInput',
          'numberFieldInput',
          'emailFieldInput',
          'phoneNumberFieldInput',
          'linkFieldInput',
          'dateFieldInput',
          'timeFieldInput',
          'dateTimeFieldInput',
          'fileUploaderFieldInput',
          'booleanFieldInput',
          'fieldChoiceOption',
          'fieldCheckboxOption',
          'fieldSelectOption',
          'fieldMultiselectOption',
        ]

        if (node.type && fieldInputTypes.includes(node.type)) {
          const attrs = node.attrs || {}
          const fieldId = attrs.fieldId || ''

          if (fieldId) {
            pageFieldIds.add(fieldId)
          }
        }

        if (node.content) {
          node.content.forEach(collectPageFieldIds)
        }
      }

      pageContent.forEach(collectPageFieldIds)

      // Теперь для каждого fieldId получаем name через layout.fields или getFieldNameByFieldId
      pageFieldIds.forEach((fieldId) => {
        const fieldSettings = layout?.fields?.[fieldId]
        const foundName = getFieldNameByFieldId(content, fieldId, layout)

        const name =
          foundName ||
          fieldSettings?.name ||
          fieldId ||
          `field-${fieldId || Math.random().toString(36).substr(2, 9)}`

        pageFieldNames.add(name)
      })

      // Фильтруем properties - только поля текущей страницы, которые не скрыты
      if (schema.properties) {
        Object.entries(schema.properties).forEach(
          ([fieldName, fieldSchema]: [string, any]) => {
            // Пропускаем скрытые поля (проверяем meta.hidden в currentSchema)
            const currentFieldSchema = currentSchema?.properties?.[fieldName]
            if (currentFieldSchema?.meta?.hidden === true) {
              return
            }

            // Включаем только поля текущей страницы
            if (pageFieldNames.has(fieldName)) {
              // Копируем схему поля
              const fieldSchemaCopy = { ...fieldSchema }

              // Если поле required, добавляем minLength: 1 для строковых полей
              // чтобы пустая строка не проходила валидацию
              const isRequired = currentSchema?.required?.includes(fieldName)
              if (
                isRequired &&
                fieldSchemaCopy.type === 'string' &&
                !fieldSchemaCopy.minLength
              ) {
                fieldSchemaCopy.minLength = 1
              }

              filteredProperties[fieldName] = fieldSchemaCopy
            }
          }
        )
      }

      schema.properties = filteredProperties

      // Фильтруем required - только поля текущей страницы, которые в currentSchema.required
      if (currentSchema?.required) {
        currentSchema.required.forEach((fieldName: string) => {
          const currentFieldSchema = currentSchema?.properties?.[fieldName]
          if (
            pageFieldNames.has(fieldName) &&
            currentFieldSchema?.meta?.hidden !== true
          ) {
            filteredRequired.push(fieldName)
          }
        })
      }

      if (filteredRequired.length > 0) {
        schema.required = filteredRequired
      } else {
        delete schema.required
      }

      // console.log('createValidationSchema:', {
      //   pageFieldNames: Array.from(pageFieldNames),
      //   currentSchemaRequired: currentSchema?.required,
      //   filteredRequired,
      //   schema,
      // })

      return schema
    },
    [compiledJsonSchema, currentSchema]
  )

  const handleSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      e.stopPropagation()

      // При сабмите всегда скроллим форму вверх
      scrollToFormTop()

      // Если есть страницы (больше одной), валидируем только последнюю страницу
      if (hasPages && pages.length > 1) {
        const isLastStep = currentStep === pages.length - 1
        if (!isLastStep) {
          // Если это не последняя страница, не обрабатываем submit
          console.warn('handleSubmit - Not on last step, ignoring submit')

          return
        }
      }

      const currentPageContent =
        hasPages && pages.length > 1
          ? pages[currentStep]
          : content.content || []

      // Создаем схему валидации для текущей страницы
      const validationSchema = createValidationSchema(currentPageContent)

      // Нормализуем значения перед валидацией (в т.ч. числовые поля)
      const valuesToValidate = normalizeValuesForValidation(
        formValues,
        validationSchema
      )

      // Валидируем с помощью Ajv
      // strict: false позволяет использовать нестандартные ключевые слова (например, meta)
      const ajv = new Ajv({ allErrors: true, strict: false })
      addFormats(ajv as any) // Добавляем поддержку форматов (email, uri и т.д.)
      const validate = ajv.compile(validationSchema)
      const isValid = validate(valuesToValidate)

      // console.log('handleSubmit - Validation result:', {
      //   isValid,
      //   errors: validate.errors,
      // })

      if (!isValid && validate.errors) {
        // Собираем ошибки валидации только для полей текущей страницы
        const errors: Record<string, string> = {}

        // Собираем имена полей на текущей странице (используем ту же логику, что и в createValidationSchema)
        const pageFieldNames = new Set<string>()
        const pageFieldIds = new Set<string>()

        // Сначала собираем fieldId из pageContent
        function collectPageFieldIds(node: JSONContent) {
          const fieldInputTypes = [
            'shortAnswerFieldInput',
            'longAnswerFieldInput',
            'numberFieldInput',
            'emailFieldInput',
            'phoneNumberFieldInput',
            'linkFieldInput',
            'dateFieldInput',
            'timeFieldInput',
            'dateTimeFieldInput',
            'fileUploaderFieldInput',
            'booleanFieldInput',
            'fieldChoiceOption',
            'fieldCheckboxOption',
            'fieldSelectOption',
            'fieldMultiselectOption',
          ]

          if (node.type && fieldInputTypes.includes(node.type)) {
            const attrs = node.attrs || {}
            const fieldId = attrs.fieldId || ''

            if (fieldId) {
              pageFieldIds.add(fieldId)
            }
          }

          if (node.content) {
            node.content.forEach(collectPageFieldIds)
          }
        }

        currentPageContent.forEach(collectPageFieldIds)

        // Теперь для каждого fieldId получаем name через layout.fields или getFieldNameByFieldId
        pageFieldIds.forEach((fieldId) => {
          const fieldSettings = layout?.fields?.[fieldId]
          const foundName = getFieldNameByFieldId(content, fieldId, layout)

          const name =
            foundName ||
            fieldSettings?.name ||
            fieldId ||
            `field-${fieldId || Math.random().toString(36).substr(2, 9)}`

          pageFieldNames.add(name)
        })

        validate.errors.forEach((error) => {
          // Извлекаем fieldName из instancePath или dataPath
          let fieldName: string | undefined
          const path =
            (error as any).instancePath || (error as any).dataPath || ''
          if (path) {
            fieldName = path.replace(/^\//, '')
          } else if ((error.params as any)?.missingProperty) {
            fieldName = (error.params as any).missingProperty
          }

          // Добавляем ошибку только если поле на текущей странице
          if (fieldName && pageFieldNames.has(fieldName)) {
            errors[fieldName] = getLocalizedErrorMessage(error)
          }
        })
        scrollToFirstErrorField(errors)

        // Объединяем ошибки от схемы с кастомными ошибками (не перезаписываем кастомные)
        setValidationErrors((prev) => ({ ...prev, ...errors }))

        return
      } else {
        // Очищаем ошибки валидации
        setValidationErrors({})
      }

      // Проверяем кастомные ошибки валидации (от PhoneInputWithCountry и т.д.)
      const hasCustomErrors = Object.keys(customFieldErrors).some(
        (fieldName) =>
          customFieldErrors[fieldName] &&
          customFieldErrors[fieldName].trim() !== ''
      )

      if (hasCustomErrors) {
        // Есть ошибки валидации от кастомных компонентов - блокируем отправку
        scrollToFirstErrorField(customFieldErrors)

        return
      }

      // Если это последняя страница, отправляем форму
      if (hasPages && pages.length > 1) {
        const isLastStep = currentStep === pages.length - 1
        if (isLastStep) {
          setIsSubmitted(true)
          if (onSubmit) {
            // Отправляем нормализованные значения (с приведёнными числами)
            onSubmit(normalizeValuesForValidation(formValues, validationSchema))
          }
        }
      } else {
        // Если нет страниц или только одна страница, отправляем форму сразу
        setIsSubmitted(true)
        if (onSubmit) {
          onSubmit(normalizeValuesForValidation(formValues, validationSchema))
        }
      }

      window.parent.postMessage({ type: 'FORM_SUBMITTED' }, '*')
    },
    [
      formValues,
      onSubmit,
      content,
      currentStep,
      createValidationSchema,
      hasPages,
      pages,
      currentSchema,
      getLocalizedErrorMessage,
      scrollToFirstErrorField,
      scrollToFormTop,
      validationErrors,
      customFieldErrors,
      layout,
      getFieldNameByFieldId,
      normalizeValuesForValidation,
    ]
  )

  // Общие вспомогательные данные для рендера
  const totalSteps = hasPages ? pages.length : 0
  const isFirstStep = totalSteps > 0 && currentStep === 0
  const isLastStep = totalSteps > 0 && currentStep === totalSteps - 1

  const handleNext = React.useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      if (!hasPages || pages.length <= 1) {
        return
      }

      // Предотвращаем отправку формы
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }

      // Валидируем текущую страницу перед переходом
      const currentPageContent = pages[currentStep]
      const validationSchema = createValidationSchema(currentPageContent)

      // Нормализуем значения перед валидацией (в т.ч. числовые поля)
      const valuesToValidate = normalizeValuesForValidation(
        formValues,
        validationSchema
      )

      // Валидируем с помощью Ajv
      const ajv = new Ajv({ allErrors: true, strict: false })
      addFormats(ajv as any) // Добавляем поддержку форматов (email, uri и т.д.)
      const validate = ajv.compile(validationSchema)
      const isValid = validate(valuesToValidate)

      if (!isValid && validate.errors) {
        // Собираем ошибки валидации только для полей текущей страницы
        const errors: Record<string, string> = {}

        // Собираем имена полей на текущей странице (используем ту же логику, что и в createValidationSchema)
        const pageFieldNames = new Set<string>()
        const pageFieldIds = new Set<string>()

        // Сначала собираем fieldId из pageContent
        function collectPageFieldIds(node: JSONContent) {
          const fieldInputTypes = [
            'shortAnswerFieldInput',
            'longAnswerFieldInput',
            'numberFieldInput',
            'emailFieldInput',
            'phoneNumberFieldInput',
            'linkFieldInput',
            'dateFieldInput',
            'timeFieldInput',
            'dateTimeFieldInput',
            'fileUploaderFieldInput',
            'fieldChoiceOption',
            'fieldCheckboxOption',
            'fieldSelectOption',
            'fieldMultiselectOption',
          ]

          if (node.type && fieldInputTypes.includes(node.type)) {
            const attrs = node.attrs || {}
            const fieldId = attrs.fieldId || ''

            if (fieldId) {
              pageFieldIds.add(fieldId)
            }
          }

          if (node.content) {
            node.content.forEach(collectPageFieldIds)
          }
        }

        currentPageContent.forEach(collectPageFieldIds)

        // Теперь для каждого fieldId получаем name через layout.fields или getFieldNameByFieldId
        pageFieldIds.forEach((fieldId) => {
          const fieldSettings = layout?.fields?.[fieldId]
          const foundName = getFieldNameByFieldId(content, fieldId, layout)

          const name =
            foundName ||
            fieldSettings?.name ||
            fieldId ||
            `field-${fieldId || Math.random().toString(36).substr(2, 9)}`

          pageFieldNames.add(name)
        })

        validate.errors.forEach((error) => {
          // Извлекаем fieldName из instancePath или dataPath
          let fieldName: string | undefined
          const path =
            (error as any).instancePath || (error as any).dataPath || ''
          if (path) {
            fieldName = path.replace(/^\//, '')
          } else if ((error.params as any)?.missingProperty) {
            fieldName = (error.params as any).missingProperty
          }

          // Добавляем ошибку только если поле на текущей странице
          if (fieldName && pageFieldNames.has(fieldName)) {
            errors[fieldName] = getLocalizedErrorMessage(error)
          }
        })
        scrollToFirstErrorField(errors)

        // Объединяем ошибки от схемы с кастомными ошибками (не перезаписываем кастомные)
        shouldClearErrorsOnStepChange.current = false
        setValidationErrors((prev) => ({ ...prev, ...errors }))

        return
      } else {
        setValidationErrors({})
      }
      // Проверяем кастомные ошибки валидации (от PhoneInputWithCountry и т.д.)
      const hasCustomErrors = Object.keys(customFieldErrors).some(
        (fieldName) =>
          customFieldErrors[fieldName] &&
          customFieldErrors[fieldName].trim() !== ''
      )

      if (hasCustomErrors) {
        // Есть ошибки валидации от кастомных компонентов - блокируем переход
        scrollToFirstErrorField(customFieldErrors)
        shouldClearErrorsOnStepChange.current = false

        return
      }

      // Если ошибок нет - очищаем все ошибки и переходим на следующую страницу
      shouldClearErrorsOnStepChange.current = false
      setValidationErrors({})
      if (currentStep < totalSteps - 1) {
        setCurrentStep((prev) => prev + 1)
        // При переходе на следующую страницу скроллим форму вверх
        scrollToFormTop()
        // Сохраняем данные при переходе между страницами (partial submit)
        if (onPartialSubmit) {
          onPartialSubmit(formValues)
        }
      }
    },
    [
      hasPages,
      pages,
      currentStep,
      createValidationSchema,
      normalizeValuesForValidation,
      formValues,
      layout,
      content,
      getLocalizedErrorMessage,
      scrollToFirstErrorField,
      scrollToFormTop,
      validationErrors,
      customFieldErrors,
      getFieldNameByFieldId,
      onPartialSubmit,
    ]
  )

  const handlePrevious = React.useCallback(() => {
    // Очищаем ошибки валидации при переходе на предыдущую страницу
    shouldClearErrorsOnStepChange.current = false
    setValidationErrors({})
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1)
      scrollToFormTop()
      // Сохраняем данные при переходе между страницами (partial submit)
      if (onPartialSubmit) {
        onPartialSubmit(formValues)
      }
    }
  }, [currentStep, scrollToFormTop, onPartialSubmit, formValues])

  // Ищем systemButtonGroup вместо submitSystemButton
  const submitButtonNode = React.useMemo(() => {
    if (!content.content) return null

    const findSystemButtonGroup = (
      nodes: JSONContent[]
    ): JSONContent | null => {
      for (const node of nodes) {
        if (
          node.type === 'buttonGroup' &&
          node.attrs?.id === 'systemButtonGroup'
        ) {
          return node
        }
        if (node.content) {
          const found = findSystemButtonGroup(node.content)
          if (found) return found
        }
      }
      return null
    }

    return findSystemButtonGroup(content.content)
  }, [content.content])

  // Функция для фильтрации скрытых полей
  const filterHiddenNodes = React.useCallback(
    (nodes: JSONContent[]): JSONContent[] => {
      return nodes.filter((node) => {
        // Исключаем systemButtonGroup (рендерится отдельно)
        if (node.type === 'buttonGroup') {
          return false
        }

        // Типы полей, которые нужно проверять на скрытость
        const fieldTypes = [
          'fieldChoiceOption',
          'fieldCheckboxOption',
          'booleanFieldInput',
          'fieldSelectOption',
          'fieldMultiselectOption',
        ]

        if (fieldTypes.includes(node.type || '')) {
          const attrs = node.attrs || {}
          const fieldId = attrs.fieldId || ''

          if (!fieldId) {
            return true // Если нет fieldId, показываем поле
          }

          // Получаем name поля
          const name =
            getFieldNameByFieldId(content, fieldId, layout) ||
            layout?.fields?.[fieldId]?.name ||
            fieldId

          // Проверяем скрытость через attrs
          const isHiddenByAttrs = attrs.hidden

          // Проверяем скрытость через currentSchema
          const fieldSchema = currentSchema?.properties?.[name]
          const isHiddenBySchema = fieldSchema?.meta?.hidden === true

          // Проверяем скрытость через layout.fields
          const fieldSettings = layout?.fields?.[fieldId]
          const isHiddenByLayout = fieldSettings?.hidden

          // Если поле скрыто хотя бы одним способом, не показываем его
          if (isHiddenByAttrs || isHiddenBySchema || isHiddenByLayout) {
            return false
          }
        }

        return true
      })
    },
    [content, layout, currentSchema]
  )

  // Контент для режима без страниц (но исключаем thankyou, systemButtonGroup и скрытые поля)
  const flatContentToRender = React.useMemo(() => {
    if (!content.content) {
      return []
    }

    let contentToFilter = content.content

    if (thankYouPage) {
      const thankYouPageIndex = content.content.findIndex(
        (node) => node.type === 'page' && node.attrs?.isThankYouPage === true
      )

      if (thankYouPageIndex !== -1) {
        contentToFilter = content.content.slice(0, thankYouPageIndex)
      }
    }

    // Фильтруем скрытые поля и systemButtonGroup
    return filterHiddenNodes(contentToFilter)
  }, [content.content, thankYouPage, filterHiddenNodes])

  // Контент текущей страницы при наличии страниц (исключаем systemButtonGroup и скрытые поля)
  const currentPageContent = React.useMemo(() => {
    if (!hasPages || pages.length === 0) {
      return []
    }
    // Фильтруем скрытые поля и systemButtonGroup из текущей страницы
    return filterHiddenNodes(pages[currentStep])
  }, [hasPages, pages, currentStep, filterHiddenNodes])

  // Признаки режимов
  const hasMultiplePages = hasPages && pages.length > 1
  const hasSinglePage = hasPages && pages.length === 1

  // Thank you контент
  const thankYouContent = thankYouPage?.content || []

  // Дефолтная страница благодарности, если нет thankYouPage
  const defaultThankYouContent: JSONContent[] = React.useMemo(() => {
    return [
      {
        type: 'heading',
        attrs: { level: 1, textAlign: 'center' },
        content: [
          {
            type: 'text',
            text: 'Thank you!',
          },
        ],
      },
      {
        type: 'paragraph',
        attrs: { textAlign: 'center' },
        content: [
          {
            type: 'text',
            text: 'Your form has been successfully submitted.',
          },
        ],
      },
    ]
  }, [])

  // Определяем, какой контент показывать после отправки
  const finalThankYouContent = (
    thankYouPage ? thankYouContent : defaultThankYouContent
  ).filter(
    (node) =>
      node.type !== 'buttonGroup' && node.attrs?.id !== 'systemButtonGroup'
  )

  const { justifyContent, renderContentButtons } = React.useMemo(() => {
    const buttonGroup = content?.content?.find(
      (node) => node.type === 'buttonGroup'
    )

    return {
      justifyContent: buttonGroup?.attrs.justifyContent || 'flex-end',
      renderContentButtons: buttonGroup?.content,
    }
  }, [content])

  // Стили системных кнопок, аналогичные RenderButton
  const getSystemButtonSx = React.useCallback(
    (attrs: Record<string, any> = {}) => {
      const {
        variant = 'contained',
        buttonColor,
        textColor,
        borderRadius = 'smooth',
        paddingTop = 10,
        paddingRight = 32,
        paddingBottom = 10,
        paddingLeft = 32,
      } = attrs

      const DEFAULT_BUTTON_BACKGROUND_COLOR = '#000000'
      const DEFAULT_BUTTON_TEXT_COLOR_CONTAINED = '#ffffff'

      const buttonBackgroundColor =
        buttonColor ||
        muiTheme.palette.primary.main ||
        DEFAULT_BUTTON_BACKGROUND_COLOR

      const resolvedTextColor =
        textColor ||
        (variant === 'contained'
          ? DEFAULT_BUTTON_TEXT_COLOR_CONTAINED
          : buttonBackgroundColor)

      const radiusMap: Record<string, string | number> = {
        sharp: 0,
        smooth: '6px',
        round: '9999px',
      }

      const base = {
        textTransform: 'none' as const,
        fontWeight: 600,
        boxShadow: 'none',
        borderRadius: radiusMap[borderRadius] ?? '6px',
        paddingTop: `${paddingTop}px`,
        paddingBottom: `${paddingBottom}px`,
        paddingLeft: `${paddingLeft}px`,
        paddingRight: `${paddingRight}px`,
      }

      if (variant === 'outlined') {
        return {
          '&&': {
            ...base,
            color: resolvedTextColor,
            borderColor: buttonColor,
            borderWidth: 1,
            borderStyle: 'solid',
            '&:hover': {
              borderColor: buttonColor,
              backgroundColor: 'transparent',
            },
          },
        }
      }

      if (variant === 'text') {
        return {
          '&&': {
            ...base,
            color: resolvedTextColor,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
        }
      }

      // contained (по умолчанию)
      return {
        '&&': {
          ...base,
          backgroundColor: buttonBackgroundColor,
          color: resolvedTextColor,
          '&:hover': {
            backgroundColor: buttonBackgroundColor,
            boxShadow: 'none',
          },
        },
      }
    },
    [muiTheme]
  )

  return (
    <ThemeProvider theme={muiTheme}>
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          ...bodyStyles,
        }}
      >
        {/* Thank you page после успешного сабмита */}
        {isSubmitted ? (
          <Box
            sx={{
              height: '100%',
              maxHeight: '100%',
              // maxWidth: '800px',
              // margin: '0 auto',
              minWidth: 500,
              padding: 3,
              ...containerStyles,
            }}
          >
            <Stack spacing={2}>
              {finalThankYouContent.map((node, index) => (
                <RenderNode
                  key={index}
                  node={node}
                  formValues={formValues}
                  onFieldChange={handleFieldChange}
                  currentSchema={currentSchema}
                  validationErrors={allValidationErrors}
                  content={content}
                  layout={layout}
                  onValidationError={handleValidationError}
                  onBlur={handleBlur}
                />
              ))}
            </Stack>
          </Box>
        ) : (
          <Box
            component="form"
            ref={formRef}
            onSubmit={handleSubmit}
            noValidate
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minWidth: { sm: '100%', md: 500 },
              // maxWidth: '800px',
              // margin: '0 auto',
              padding: 3,
              ...containerStyles,
            }}
          >
            {/* Stepper для многостраничного режима */}
            {layout.showPagination && hasMultiplePages && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: 4,
                }}
              >
                <Stepper
                  activeStep={currentStep}
                  sx={{
                    width: 'auto',
                    '& .MuiStep-root': {
                      padding: '4px',
                    },
                    '& .MuiStepLabel-root': {
                      padding: 0,
                      paddingRight: 0,
                    },
                    '& .MuiStepLabel-iconContainer': {
                      paddingRight: 0,
                    },
                    '& .MuiStepConnector-root': {
                      display: 'block',
                    },
                    '& .MuiStepConnector-line': {
                      borderTopWidth: '2px',
                      minWidth: '20px',
                    },
                    '& .MuiStepIcon-root': {
                      width: '20px',
                      height: '20px',
                      fontSize: '0.875rem',
                    },
                    '& .MuiStepIcon-text': {
                      fontSize: '0.75rem',
                      fontWeight: 700,
                    },
                  }}
                >
                  {pages.map((_, index) => (
                    <Step key={index}>
                      <StepLabel />
                    </Step>
                  ))}
                </Stepper>
              </Box>
            )}
            {/* Основной контент формы */}
            <Box
              sx={{
                maxHeight: '100%',
                overflowY: 'auto',
                marginBottom: hasMultiplePages ? 4 : 0,
              }}
            >
              <Stack spacing={0}>
                {hasMultiplePages
                  ? currentPageContent.map((node, index) => (
                      <RenderNode
                        key={index}
                        node={node}
                        formValues={formValues}
                        onFieldChange={handleFieldChange}
                        currentSchema={currentSchema}
                        validationErrors={allValidationErrors}
                        onValidationError={handleValidationError}
                        content={content}
                        layout={layout}
                        onBlur={handleBlur}
                      />
                    ))
                  : hasSinglePage
                    ? filterHiddenNodes(pages[0]).map((node, index) => (
                        <RenderNode
                          key={index}
                          node={node}
                          formValues={formValues}
                          onFieldChange={handleFieldChange}
                          currentSchema={currentSchema}
                          validationErrors={allValidationErrors}
                          onValidationError={handleValidationError}
                          content={content}
                          layout={layout}
                          onBlur={handleBlur}
                        />
                      ))
                    : flatContentToRender.map((node, index) => (
                        <RenderNode
                          key={index}
                          node={node}
                          formValues={formValues}
                          onFieldChange={handleFieldChange}
                          currentSchema={currentSchema}
                          validationErrors={allValidationErrors}
                          onValidationError={handleValidationError}
                          content={content}
                          layout={layout}
                          onBlur={handleBlur}
                        />
                      ))}
              </Stack>
            </Box>
            {/* Navigation / Submit buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent }}>
              {renderContentButtons.map((buttonCluster) => (
                <Box
                  key={buttonCluster.attrs.role}
                  sx={{
                    display: 'flex',
                    columnGap: '8px',
                  }}
                >
                  {buttonCluster.content
                    ?.filter(() => {
                      if (buttonCluster.attrs.role === 'actions') return true

                      return currentStep > 0
                    })
                    ?.filter((btn) => {
                      if (buttonCluster.attrs.role === 'navigation') return true

                      return isLastStep
                        ? btn.attrs.id === 'submitSystemButton'
                        : btn.attrs.id === 'nextSystemButton'
                    })
                    .map((btn) => (
                      <Button
                        key={btn.attrs.id}
                        variant={btn.attrs.variant}
                        type={
                          btn.attrs.id === 'submitSystemButton'
                            ? 'submit'
                            : undefined
                        }
                        onClick={
                          btn.attrs.id === 'prevSystemButton'
                            ? handlePrevious
                            : btn.attrs.id === 'nextSystemButton'
                              ? handleNext
                              : handleSubmit
                        }
                        sx={getSystemButtonSx(btn.attrs)}
                      >
                        {btn.attrs.text}
                      </Button>
                    ))}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  )
}
